import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import type { Dayjs } from 'dayjs'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getTickets } from '@/modules/Ticket/services'
import { formatDate } from '@/modules/Ticket/types/utils'
import {
    createTicketColumns,
    createTicketFilters,
    TICKET_FILTER_DEFAULTS,
    type TicketFilters,
} from '@/modules/Ticket/utils/ticket-list-config'

interface UseTicketListOptions {
    filterTime: [Dayjs | null, Dayjs | null]
}

/**
 * Business logic hook for Ticket List
 * Manages data fetching, pagination, sorting, and filtering
 */
export const useTicketList = ({ filterTime }: UseTicketListOptions) => {
    const { t } = useTranslation('ticket')

    // Calculate date range from filterTime
    const dateRange = useMemo<[string, string] | []>(() => {
        if (filterTime[0] && filterTime[1]) {
            const start = formatDate(filterTime[0])
            const end = formatDate(filterTime[1])
            if (start && end) {
                return [start, end]
            }
        }
        return []
    }, [filterTime])

    // URL params management
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<TicketFilters>({
        defaults: { sort_by: 'ticket_id', sort_order: 'desc', page_size: 20 },
        filterDefaults: TICKET_FILTER_DEFAULTS,
    })

    // Form for filter controls
    const { control } = useForm<TicketFilters>({
        defaultValues: filters,
    })

    // Inject date range into query params
    const queryParamsWithDateRange = useMemo(() => {
        let existingFilters: Record<string, unknown> = {}
        if (queryParams.filters) {
            try {
                existingFilters = JSON.parse(atob(queryParams.filters))
            } catch {
                // Ignore parse errors
            }
        }

        const mergedFilters = {
            ...existingFilters,
            ...(dateRange.length === 2 && {
                ticket_requested_at: dateRange,
            }),
        }

        // Clean empty values
        const cleanFilters: Record<string, unknown> = {}
        Object.entries(mergedFilters).forEach(([key, value]) => {
            if (value === undefined || value === null) return
            if (Array.isArray(value) && (value as unknown[]).length === 0)
                return
            if (typeof value === 'string' && value === '') return
            cleanFilters[key] = value
        })

        return {
            ...queryParams,
            filters:
                Object.keys(cleanFilters).length > 0
                    ? btoa(JSON.stringify(cleanFilters))
                    : '',
        }
    }, [queryParams, dateRange])

    // Unique key for triggering re-fetch
    const dateRangeKey = dateRange.join('|')

    // Fetch tickets data
    const { data, renderState, isFetching } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.TICKETS, queryParamsWithDateRange, dateRangeKey],
        queryFn: () => getTickets(queryParamsWithDateRange),
        placeholderData: true,
    })

    // Memoized columns & filters
    const columns = useMemo(() => createTicketColumns(t), [t])
    const filterItems = useMemo(() => createTicketFilters(t), [t])

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )

    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        // Data
        tickets: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),
        isFetching,

        // Params & Filters
        params,
        control,
        setFilter,
        searchInputProps,

        // Configs
        columns,
        filterItems,

        // Handlers
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,

        // Translation
        t,
    }
}

export default useTicketList
